import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Magnetic, scrollToId } from "../lib/motion";
import { Sparkles } from "lucide-react";

const LINKS = [
  { label: "Capabilities", id: "services" },
  { label: "Nonprofit", id: "nonprofit" },
  { label: "Scheduling", route: "/scheduling" },
  { label: "Infrastructure", id: "data-center" },
  { label: "Intelligence", id: "ai" },
  { label: "Impact", id: "results" },
];

const scrollTo = scrollToId;

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const go = (l) => {
    if (l.route) navigate(l.route);
    else if (pathname !== "/") navigate(`/#${l.id}`);
    else scrollTo(l.id);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className="fixed inset-x-0 top-0 z-[70] flex justify-center px-4 pt-4"
    >
      <nav
        className={`flex w-full max-w-7xl items-center justify-between rounded-full px-5 py-3 transition-all duration-500 ${
          scrolled ? "glass" : "border border-transparent"
        }`}
        data-testid="navbar"
      >
        <button
          onClick={() => (pathname !== "/" ? navigate("/") : scrollTo("hero"))}
          className="flex items-center gap-2.5"
          data-testid="nav-logo"
        >
          <span className="relative flex h-7 w-7 items-center justify-center">
            <span className="absolute inset-0 rounded-md bg-gradient-to-br from-nexis-blue to-nexis-purple opacity-90" />
            <span className="relative font-display text-sm font-bold text-black">N</span>
          </span>
          <span className="font-display text-lg font-semibold tracking-tight">
            Nexis<span className="text-nexis-blue">.</span>team
          </span>
        </button>

        <div className="hidden items-center gap-1 md:flex">
          {LINKS.map((l) => (
            <button
              key={l.label}
              onClick={() => go(l)}
              className="rounded-full px-4 py-2 text-sm text-white/60 transition-colors hover:text-white"
              data-testid={`nav-link-${l.route ? l.route.slice(1) : l.id}`}
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <Magnetic className="hidden sm:block">
            <button
              onClick={() => navigate("/nexsys")}
              className="group relative flex items-center gap-2 overflow-hidden rounded-full border border-nexis-blue/40 bg-nexis-blue/10 px-4 py-2.5 text-sm font-medium text-nexis-blue transition-colors hover:bg-nexis-blue/20"
              data-testid="nav-nexsys"
            >
              <Sparkles className="h-3.5 w-3.5" />
              NexSys
              <span className="hidden lg:inline text-white/50">Command Center</span>
            </button>
          </Magnetic>
          <Magnetic className="hidden sm:block">
            <button
              onClick={() => (pathname !== "/" ? navigate("/#contact") : scrollTo("contact"))}
              className="group relative overflow-hidden rounded-full bg-white px-5 py-2.5 text-sm font-medium text-black transition-transform"
              data-testid="nav-cta"
            >
              <span className="relative z-10">Start a conversation</span>
            </button>
          </Magnetic>
          <button
            className="flex h-10 w-10 items-center justify-center rounded-full glass md:hidden"
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-menu-toggle"
            aria-label="Menu"
          >
            <div className="space-y-1.5">
              <span className={`block h-px w-5 bg-white transition-transform ${open ? "translate-y-[6px] rotate-45" : ""}`} />
              <span className={`block h-px w-5 bg-white transition-opacity ${open ? "opacity-0" : ""}`} />
              <span className={`block h-px w-5 bg-white transition-transform ${open ? "-translate-y-[6px] -rotate-45" : ""}`} />
            </div>
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="glass absolute left-4 right-4 top-20 rounded-3xl p-4 md:hidden"
            data-testid="mobile-menu"
          >
            {[...LINKS, { label: "Contact", id: "contact" }].map((l) => (
              <button
                key={l.label}
                onClick={() => {
                  go(l);
                  setOpen(false);
                }}
                className="block w-full rounded-xl px-4 py-3 text-left text-white/70 hover:bg-white/5 hover:text-white"
                data-testid={`mobile-link-${l.route ? l.route.slice(1) : l.id}`}
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => navigate("/nexsys")}
              className="mt-1 flex w-full items-center gap-2 rounded-xl bg-nexis-blue/10 px-4 py-3 text-left font-medium text-nexis-blue"
              data-testid="mobile-nexsys"
            >
              <Sparkles className="h-4 w-4" /> NexSys Command Center
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
